import { Link } from '@tanstack/react-router';
import { Play } from 'lucide-react';

import type { ModuleWithProgress } from '@/lib/api/lms';

function MiniRing({ pct }: { pct: number }) {
  const size = 44;
  const stroke = 4;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (pct / 100) * c;
  return (
    <div className="relative inline-flex shrink-0 items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <defs>
          <linearGradient id="module-strip-ring" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#ec4899" />
            <stop offset="100%" stopColor="#8b5cf6" />
          </linearGradient>
        </defs>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#ede9fe" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="url(#module-strip-ring)" strokeWidth={stroke}
          strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round" className="transition-all duration-500" />
      </svg>
      <span className="absolute text-[11px] font-bold text-[#111827]">{pct}%</span>
    </div>
  );
}

/** First in_progress topic, else first not_started, else first — the module's resume target. */
function moduleResumeTopicId(module: ModuleWithProgress): string | null {
  const sorted = [...module.topics].sort((a, b) => a.sort_order - b.sort_order);
  return (
    sorted.find((t) => t.status === 'in_progress')?.id ??
    sorted.find((t) => t.status === 'not_started')?.id ??
    sorted[0]?.id ??
    null
  );
}

export function ModuleSummaryStrip({ batchId, module, pct }: {
  batchId: string; module: ModuleWithProgress; pct: number;
}) {
  const completed = module.topics.filter((t) => t.status === 'completed').length;
  const current = module.topics.filter((t) => t.status === 'in_progress').length;
  const remaining = module.topics.filter((t) => t.status === 'not_started').length;
  const resumeId = moduleResumeTopicId(module);
  const isComplete = module.topic_count > 0 && completed === module.topic_count;

  return (
    // The row is deliberately inline-flex and self-start: this strip summarises
    // one module, so it should be as wide as its own content. Stretching it to
    // the container width stranded the CTA against the far edge with a large
    // empty gutter, which read as a layout bug rather than as breathing room.
    <div className="mt-3 mb-3 inline-flex max-w-full flex-wrap items-center gap-x-3 gap-y-2 rounded-[14px] border border-[#ececec] bg-white px-3.5 py-2.5">
      <MiniRing pct={pct} />
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[13px] font-medium">
        <span className="text-[#16a34a]">{completed} done</span>
        <span className="size-1 rounded-full bg-[#d1d5db]" />
        <span className="text-[#8b5cf6]">{current} in progress</span>
        <span className="size-1 rounded-full bg-[#d1d5db]" />
        <span className="text-[#9ca3af]">{remaining} left</span>
      </div>
      {resumeId && (
        <Link
          to="/courses/$batchId/topics/$topicId"
          params={{ batchId, topicId: resumeId }}
          className="flex h-8 shrink-0 items-center gap-1.5 rounded-[10px] px-3.5 text-[13px] font-semibold text-white transition-opacity hover:opacity-90"
          style={{ background: 'linear-gradient(158.06deg, #8b5cf6 0%, #c084fc 100%)' }}
        >
          <Play className="size-3.5 translate-x-px fill-white text-white" />
          {isComplete ? 'Review' : 'Continue'}
        </Link>
      )}
    </div>
  );
}
